import type { CSSProperties } from 'react'
import { displayAmount, getSmartDecimals } from 'src/utils/displayAmount'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import { useCountUp } from './useCountUp'

export type WinTier = 'win' | 'big' | 'mega'

type Props = Readonly<{
  /** When true, the pill shows the (animated) payout amount; otherwise it shows "WINNER". */
  isPayout: boolean
  /** Final payout amount in player currency units (already including boosters). */
  payoutAmount: number
  /** Currency symbol (e.g. "$", "€") prepended to the payout amount. */
  currencySymbol: string
  /** Win tier — drives pill size, glow, and whether the particle burst fires. */
  tier: WinTier
  /** Stagger delay so multiple pills cascade in instead of all popping at once. */
  delayMs: number
}>

// Eight evenly-spaced burst directions. Pre-rendered so the ::before
// dance uses simple `:nth-child` keyed off `--particle-angle`.
const PARTICLE_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315]

/**
 * Settled-round call-out that lives above each winning option:
 *   • Player-bet-on-winner → animated payout amount with metallic-gold
 *     typography. Scales up at `big`/`mega` tiers (≥10x / ≥25x stake).
 *   • Just a winning option → "WINNER" tag in the same chassis.
 *
 * Wraps the pill + a sibling particle-burst container so the sparks
 * can emanate outside the pill's own `overflow: hidden` (which clips
 * the sheen sweep). Both pill entry, sheen, count-up, and particles
 * sequence off `delayMs` so the staggered cascade across the board
 * stays in lockstep on every winning option.
 */
export function WinPill({ isPayout, payoutAmount, currencySymbol, tier, delayMs }: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  // Lock the displayed precision based on the *target* so the count-up
  // doesn't flicker through different decimal counts as it climbs.
  const decimals = isPayout ? getSmartDecimals(payoutAmount) : 2
  // Start counting ~220ms after pill entry so the pop is the first
  // beat and the rolling number is the second.
  const animated = useCountUp(isPayout ? payoutAmount : 0, 700, delayMs + 220)

  const text = isPayout
    ? `${currencySymbol}${displayAmount(animated, true, decimals)}`
    : t('Winner')

  const tierClass =
    tier === 'mega'
      ? ' chip-panel__opt-win-pill-wrap--mega'
      : tier === 'big'
        ? ' chip-panel__opt-win-pill-wrap--big'
        : ''

  return (
    <span
      className={`chip-panel__opt-win-pill-wrap${tierClass}`}
      style={{ '--win-pill-delay': `${delayMs}ms` } as CSSProperties}
    >
      <span
        className={
          isPayout
            ? 'chip-panel__opt-win-pill chip-panel__opt-win-pill--payout'
            : 'chip-panel__opt-win-pill'
        }
      >
        <span className="chip-panel__opt-win-pill-text">{text}</span>
      </span>
      {isPayout && (
        <span className="chip-panel__opt-win-pill-particles" aria-hidden="true">
          {PARTICLE_ANGLES.map((angle) => (
            <span
              key={angle}
              className="chip-panel__opt-win-pill-particle"
              style={{ '--particle-angle': `${angle}deg` } as CSSProperties}
            />
          ))}
        </span>
      )}
    </span>
  )
}
